import {
  ClientRole,
  ClientRoleOptions,
  IAgoraRTCRemoteUser,
  LiveStreamingTranscodingConfig,
  LiveStreamingTranscodingImage,
  LiveStreamingTranscodingUser,
  UID,
  VideoEncoderConfiguration,
  VideoEncoderConfigurationPreset
} from 'agora-rtc-sdk-ng';

import { EventUserSchema, InviteeSchema, OnStageUser, RTCUser, UserRole, VideoLayout, VideoSize } from 'types';
import { ACTIVE_VOLUME_THRESHOLD, VIDEO_LAYOUT } from 'utils';

import { TRANSCODING_USER_AVATAR_SIZE, TRANSCODING_USER_AVATAR_URL } from './constants';
import { getEventURL, hashUsername } from './helpers';

export const generateInvitationLink = (channelName: string, invitee: InviteeSchema) =>
  `${getEventURL(channelName, invitee.eventCode, true)}/${invitee.inviteeCode}`;

export const generateInvitationText = (channelName: string, eventName: string, invitee: InviteeSchema) =>
  `Hey ${invitee.name} - please join this channel to talk about "${eventName}" ${generateInvitationLink(
    channelName,
    invitee
  )}`;

export const mapUserRoleToAgoraClientRole = (userRole: UserRole): ClientRole => {
  switch (userRole) {
    case UserRole.HOST:
    case UserRole.MODERATOR:
    case UserRole.SPEAKER:
      return 'host';

    case UserRole.VIEWER:
      return 'audience';

    default:
      return 'audience';
  }
};

export const mapAgoraRemoteUserToRTCUser = (remoteUser: IAgoraRTCRemoteUser): RTCUser => ({
  uid: remoteUser.uid,
  hasAudio: remoteUser.hasAudio,
  hasVideo: remoteUser.hasVideo,
  audioTrack: remoteUser.audioTrack,
  videoTrack: remoteUser.videoTrack,
  isSpeaking: (remoteUser.audioTrack?.getVolumeLevel() || 0) * 100 > ACTIVE_VOLUME_THRESHOLD
});

export const mergeEventUserAndRTCUser = (
  eventUser: EventUserSchema,
  rtcUser?: RTCUser,
  rtmUID?: string
): OnStageUser => ({
  username: eventUser.username,
  name: eventUser.name,
  image: eventUser.avatar,
  role: eventUser.role as UserRole.HOST | UserRole.SPEAKER | UserRole.MODERATOR,
  joined: !!rtcUser || !!rtmUID,
  member: !!eventUser.member,
  isSpeaking: !!rtcUser?.isSpeaking,
  audioMuted: !rtcUser?.hasAudio,
  videoMuted: !rtcUser?.hasVideo,
  audioTrack: rtcUser?.audioTrack,
  videoTrack: rtcUser?.videoTrack
});

export const getVideoLayout = (usersCount: number): VideoLayout => {
  if (usersCount in VIDEO_LAYOUT) return VIDEO_LAYOUT[usersCount];

  throw Error('video layout is not supported for this amount of users');
};

const getTranscodingUsers = (stageUsers: OnStageUser[], size: VideoSize): LiveStreamingTranscodingUser[] => {
  let result: LiveStreamingTranscodingUser[] = [];

  const uids: UID[] = stageUsers.filter(user => !!user.joined).map(user => hashUsername(user.username));

  if (uids.length === 0) return [];

  const validLayout = getVideoLayout(uids.length);

  validLayout.grid.forEach((row, rowIndex) => {
    // eslint-disable-next-line consistent-return
    row.forEach((cell, colIndex) => {
      const userIndex = rowIndex * validLayout.shape[1] + colIndex;
      const isLastOfThreeUsers = uids.length === 3 && userIndex === 2;
      const isLastOfFiveUsers = uids.length === 5 && userIndex === 4;
      const isCentered = isLastOfThreeUsers || isLastOfFiveUsers;

      if (uids.length < userIndex + 1) return result;

      const uid = uids[userIndex];
      let cellSize = {
        width: size.width / validLayout.shape[1],
        height: Math.floor(size.height / validLayout.shape[0])
      };

      let offsetX = cellSize.width * colIndex;
      const offsetY = cellSize.height * rowIndex;

      if (isCentered) {
        cellSize = { ...cellSize, width: size.width / validLayout.shape[1] };
        offsetX = size.width / (validLayout.shape[1] * 2);
      }

      result = [
        ...result,
        {
          uid,
          alpha: 1,
          zOrder: 1,
          width: cellSize.width,
          height: cellSize.height,
          x: offsetX,
          y: offsetY
        }
      ];
    });
  });

  return result;
};

// set width and height as 1 so as to hide user video when muted
// REFERENCE: https://docs.agora.io/en/Interactive%20Broadcast/cdn_streaming_web_ng?platform=Web#api-reference
export const filterTranscodingUser = (
  transcodingUser: LiveStreamingTranscodingUser,
  stageUser?: OnStageUser
): LiveStreamingTranscodingUser => {
  const videoMuted = stageUser?.videoMuted;
  const height = videoMuted ? 1 : transcodingUser.height;
  const width = videoMuted ? 1 : transcodingUser.width;
  const { x, y } = transcodingUser;

  return {
    ...transcodingUser,
    height: height ? Math.floor(height) : undefined,
    width: width ? Math.floor(width) : undefined,
    x: x ? Math.floor(x) : 0,
    y: y ? Math.floor(y) : 0
  };
};

export const getTranscodingImage = (
  transcodingUser: LiveStreamingTranscodingUser,
  stageUser: OnStageUser
): LiveStreamingTranscodingImage => {
  const { videoMuted } = stageUser;
  const { x: userX, y: userY, width: userWidth, height: userHeight } = transcodingUser;

  const height = videoMuted ? TRANSCODING_USER_AVATAR_SIZE : 1;
  const width = videoMuted ? TRANSCODING_USER_AVATAR_SIZE : 1;
  const x = Math.floor((userX as number) + (userWidth as number) / 2 - width / 2) || 0;
  const y = Math.floor((userY as number) + Math.floor((userHeight as number) / 2) - height / 2) || 0;
  const alpha = videoMuted ? 1.0 : 0.0;

  return {
    url: TRANSCODING_USER_AVATAR_URL,
    alpha,
    height,
    width,
    x,
    y
  };
};

export const getTranscodingConfig = (
  stageUsers: OnStageUser[],
  resolution: VideoEncoderConfigurationPreset = '720p'
): LiveStreamingTranscodingConfig => {
  const usersMap: Record<string, OnStageUser> = stageUsers.reduce(
    (target, user) => ({
      ...target,
      [hashUsername(user.username).toString()]: user
    }),
    {}
  );

  switch (resolution) {
    // SOURCE: Other resolution configs can be found from here: https://docs.agora.io/en/Interactive%20Broadcast/cdn_streaming_web_ng?platform=Web
    case '720p':
    default: {
      let transcodingUsers: LiveStreamingTranscodingUser[] = getTranscodingUsers(stageUsers, {
        width: 720,
        height: 1280
      });
      const images: LiveStreamingTranscodingImage[] = transcodingUsers.map(transcodingUser =>
        getTranscodingImage(transcodingUser, usersMap[transcodingUser.uid.toString()])
      );

      transcodingUsers = transcodingUsers.map(user => filterTranscodingUser(user, usersMap[user.uid.toString()]));

      return {
        width: 720,
        height: 1280,
        videoBitrate: 2260,
        videoFrameRate: 15,
        lowLatency: true,
        audioSampleRate: 48000,
        audioBitrate: 48,
        audioChannels: 1,
        videoGop: 30,
        videoCodecProfile: 100,
        backgroundColor: 0x222222,
        transcodingUsers,
        images
      };
    }
  }
};

export const getVideoEncoderConfig = () => {
  const videoEncoderConfig: VideoEncoderConfiguration | VideoEncoderConfigurationPreset = '480p';

  return videoEncoderConfig;
};

// Use standard audience latency (not premium)
// DOC: https://docs.agora.io/en/Interactive%20Broadcast/API%20Reference/web_ng/enums/audiencelatencyleveltype.html
// NOTE: this only affects when clientRole is audience
export const getClientRoleOptions = (role?: ClientRole): ClientRoleOptions | undefined => {
  if (role === 'audience') {
    return {
      level: 1
    };
  }

  return undefined;
};
